import { useCallback, useRef } from 'react';

/**
 * Returns a stable function whose identity never changes across renders but
 * which always calls the latest version of `fn` (so it sees the latest props,
 * state, and closures). This is a userland approximation of React's
 * `useEffectEvent` RFC.
 *
 * Use this for event handlers that:
 *   - need access to the latest render-scope values (props, state, hook results)
 *   - are passed to memoized children, used in `useCallback`/`useEffect`
 *     dependency arrays, or otherwise need a stable identity
 *
 * Do NOT use this for functions that are already stable (e.g. setters from
 * `useState`, or callbacks already wrapped in `useCallback([])`).
 *
 * Note: This intentionally writes to the ref during render. React's own
 * `useEffectEvent` updates the ref from an insertion/layout effect; doing it
 * here is a pragmatic userland approximation and is safe as long as the
 * returned function is only invoked from events (or effects), never during
 * render.
 */
const useEvent = <TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => TReturn,
): ((...args: TArgs) => TReturn) => {
  // Stable identity is this hook's public contract.
  // So here we are telling React compiler not to run useCallback optimizations on this hook.
  'use no memo';

  const ref = useRef(fn);
  ref.current = fn;

  return useCallback((...args: TArgs) => ref.current(...args), []);
};

export default useEvent;
